import React, { useEffect, useRef, useState, lazy, Suspense } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { useAuthContext } from "contexts/AuthContext"
import { useThemeContext } from "contexts/ThemeContext"
import { useAlertQueue } from "contexts/AlertQueueContext"
import { useStudentHub } from "contexts/StudentHubContext"
import usePageVisibility from "hooks/usePageVisibility"
import useNotificationApi from "modules/notifications"
import useRewardsApi from "modules/rewards"
import { routes } from "common/navigationRoutes"
import { changeMenu, menuOpen } from "utils/sounds"
import useSound from "use-sound"
import { DEVICE_TYPES, LOCAL_STORAGE_PREFIX, SCREEN_ID } from "common/constants"
import { HeaderUI } from "./Header.ui"
import { useTranslation } from "react-i18next"
import ActivityTools from "components/Activity/ActivityTools"
import NotificationsMenu from "components/NotificationsMenu"
import GroupsButton from "pages/student/groups/GroupsButton"
import { fontsImages } from "utils/images"
import { fontFamilyOptions } from "common/textEditorConstants"
import { Modal, useTheme } from "@mui/material"
import Image from "components/Image"
import { styles } from "./Header.styles"
import TeacherNotificationsMenu from "components/TeacherNotificationsMenu"

const RewardsItem = lazy(() => import("pages/student/rewards/RewardsItem"))
const FontSettingsModal = lazy(() =>
  import("components/Tools/FontSettingsModal")
)

const getScreenId = (screenName) => {
  const deviceTypeId = DEVICE_TYPES.WEB
  return SCREEN_ID[deviceTypeId][screenName]
}

export const StudentHeaderView = ({ onMenuClick = () => {} }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    userData,
    updateUserData,
    selectedGroupData,
    logout,
    audioSettings,
    cacheRewardsNotifications,
  } = useAuthContext()
  const location = useLocation()
  const currentRoute = location.pathname
  const { fontFamily } = useThemeContext()
  const [fontSettingOpen, setFontSettingOpen] = useState(false)
  const { fontSettings, setFontSettings } = useState({})
  const [notificationsCount, setNotificationCount] = useState(0)
  const [messagesCount, setMessagesCount] = useState(0)
  const { getNotificationsNumber } = useNotificationApi()
  const pagesLeft = [
    ...(process.env.REACT_APP_ANNOUNCEMENT_HIDDEN === "true"
      ? [
          {
            label: "classes",
            path: routes.GROUPS,
          },
        ]
      : [
          {
            label: "announcements",
            path: routes.ANNOUNCEMENTS,
          },
        ]),
    {
      label: "missions",
      path: routes.MISSIONS,
    },
  ]
  const pagesRight = [
    {
      label: "grades",
      path: routes.GRADES,
    },
    {
      label: "Games",
      path: routes.NEW_GAMES,
    },
  ]
  const isPageVisible = usePageVisibility()
  const isBusy = useRef(false)
  const [playMenuSound] = useSound(menuOpen, { volume: 0.5, html5: true })
  const [playChangeMenuSound] = useSound(changeMenu, {
    volume: 0.5,
    html5: true,
  })
  const { getUnreadRewards } = useRewardsApi()
  const [openRewardNotification, setOpenRewardNotification] = useState(false)
  const [unreadRewards, setUnreadRewards] = useState([])
  const { setActiveId } = useAlertQueue()
  const [notificationLastUpdate, setNotificationLastUpdate] = useState(
    new Date().toISOString()
  )
  const theme = useTheme()
  const activityToolsRef = useRef(null)
  const { isStudentHubActive, setSessionActivityLog } = useStudentHub()
  const firstLoadRef = useRef(true)

  useEffect(() => {
    firstLoadRef.current = true
    const handleOpenActivityTools = () => {
      activityToolsRef.current?.openTools(true) // Pasar true para resaltar el botón de dark mode
    }
    window.addEventListener("openActivityTools", handleOpenActivityTools)
    return () => {
      firstLoadRef.current = false
      window.removeEventListener("openActivityTools", handleOpenActivityTools)
    }
  }, [])

  useEffect(() => {
    const currentRouteName = currentRoute?.replace("/", "")
    // setCurrentScreen(currentRouteName)
    if (currentRouteName !== "activity") {
      setSessionActivityLog({
        viewId: getScreenId("default"),
      })
    }
    if (!isBusy.current) {
      const lastFetch = localStorage.getItem(
        `${LOCAL_STORAGE_PREFIX}notifications_last_fetch`
      )
      const now = new Date().getTime()
      const twoMinutes = 2 * 60 * 1000
      if (
        !lastFetch ||
        now - parseInt(lastFetch) >= twoMinutes ||
        firstLoadRef.current
      ) {
        firstLoadRef.current = false
        fetchNotificationCount()
      }
    }
    if (currentRouteName !== "activity" && !isStudentHubActive)
      readRewardsNotifications()
  }, [currentRoute, isStudentHubActive])

  useEffect(() => {
    const lastFetch = localStorage.getItem(
      `${LOCAL_STORAGE_PREFIX}notifications_last_fetch`
    )
    const now = new Date().getTime()
    const twoMinutes = 2 * 60 * 1000
    if (
      isPageVisible &&
      !isBusy.current &&
      now - parseInt(lastFetch) >= twoMinutes
    ) {
      fetchNotificationCount()
    }
  }, [isPageVisible])

  const changeTutorialsPreference = (evt) => {
    evt.stopPropagation()
    updateUserData({
      newUserData: {
        allowTutorials: !userData?.allowTutorials,
      },
    })
  }

  const changeAudioEffectsPreference = (evt) => {
    evt.stopPropagation()
    updateUserData({
      newUserData: {
        allowSoundEffects: !userData?.allowSoundEffects,
      },
    })
  }

  const fetchNotificationCount = () => {
    isBusy.current = true
    const languageId = userData?.isB2C
      ? userData?.b2C?.languageToLearn
      : selectedGroupData?.learningLanguageId
    getNotificationsNumber({ languageId })
      .then((response) => {
        const ncount = response.notifications || 0
        const mcount = response.messages || 0
        setNotificationCount(ncount)
        setMessagesCount(mcount)
        setNotificationLastUpdate(new Date().toISOString())
        if (process.env.REACT_APP_ENVIORNMENT !== "production") {
          const now = new Date().getTime()
          localStorage.setItem(
            `${LOCAL_STORAGE_PREFIX}notifications_last_fetch`,
            now.toString()
          )
        }
      })
      .finally(() => {
        isBusy.current = false
      })
  }

  const readRewardsNotifications = async () => {
    if (cacheRewardsNotifications) return
    const languageId = userData?.isB2C
      ? userData?.languageToLearn
      : selectedGroupData?.learningLanguageId
    getUnreadRewards({ languageId })
      .then((response) => {
        if (response?.length > 0) {
          setActiveId("rewardNotification")
          setOpenRewardNotification(true)
          setUnreadRewards(response)
        }
      })
      .catch((ex) => {
        console.log("🚀 ~ getUnreadRewards ~ ex:", ex)
        setOpenRewardNotification(false)
        setUnreadRewards([])
      })
  }

  const openDrawerMenu = () => {
    if (audioSettings?.enabledAudioEffects) {
      playMenuSound()
    }
    onMenuClick()
  }

  const handleNavLinkClick = (event, menu) => {
    if (currentRoute === menu.path) {
      event.preventDefault()
      if (audioSettings?.enabledAudioEffects) {
        playChangeMenuSound()
      }
      navigate(0)
      return
    }
    if (audioSettings?.enabledAudioEffects) {
      playChangeMenuSound()
    }
  }

  const changeDarkModePreference = (evt) => {
    const isDarkMode = userData?.uiSettings?.theme?.toLowerCase() === "dark"
    evt.stopPropagation()
    updateUserData({
      newUserData: {
        uiSettings: {
          theme: isDarkMode ? "Light" : "Dark",
        },
      },
    })
    localStorage.setItem("smash-prefers-dark-mode", (!isDarkMode).toString())
    // Usar la función global reutilizable para aplicar el dark mode
    if (window.applyDarkMode) {
      window.applyDarkMode()
    }
  }

  return (
    <React.Fragment>
      <Modal
        open={openRewardNotification}
        onClose={() => setOpenRewardNotification(false)}
      >
        <Suspense fallback={null}>
          <RewardsItem
            params={{
              userItemId: 0,
              unreads: "1",
              items: unreadRewards,
              onClose: (evt) => {
                setActiveId(null)
                evt.stopPropagation()
                evt.preventDefault()
                setOpenRewardNotification(false)
                fetchNotificationCount()
              },
            }}
          />
        </Suspense>
      </Modal>
      <Suspense fallback={null}>
        <FontSettingsModal
          open={fontSettingOpen}
          onClose={() => setFontSettingOpen(false)}
          fontSettings={fontSettings}
          setFontSettings={setFontSettings}
        />
      </Suspense>
      <HeaderUI
        currentRoute={currentRoute}
        onOpenDrawer={openDrawerMenu}
        pagesLeft={pagesLeft}
        pagesRight={pagesRight}
        onNavLinkClick={handleNavLinkClick}
        centerContent={
          <NavLink
            to={routes.MISSIONS}
            onClick={(e) => handleNavLinkClick(e, { path: routes.MISSIONS })}
          >
            <Image name="logoAppBar" width={"50px"} height={"auto"} />
          </NavLink>
        }
        userLeftContent={<GroupsButton />}
        userRightContent={
          <>
            <TeacherNotificationsMenu
              total={messagesCount}
              sx={styles.headerRightButton}
              notificationLastUpdate={notificationLastUpdate}
            />
            <NotificationsMenu
              total={notificationsCount}
              sx={styles.headerRightButton}
              notificationLastUpdate={notificationLastUpdate}
            />
            <ActivityTools
              ref={activityToolsRef}
              hideTools
              sx={{
                menuButton: styles.headerRightButton,
                menuIcon: styles.menuIcon(theme),
                toolsMenu: { mx: 0 },
              }}
              extraTools={[
                {
                  order: 10,
                  name: t("faq_and_tutorials"),
                  onClick: () => {
                    navigate("/training")
                  },
                  icon: <Image name="faq" width={32} height={32} />,
                },
                {
                  order: 99,
                  name: t("select_your_font"),
                  onClick: () => setFontSettingOpen(true),
                  icon: (
                    <Image
                      src={
                        fontsImages[
                          fontFamilyOptions?.find((f) => f.name === fontFamily)
                            ?.icon || "Poppins"
                        ]
                      }
                      width={32}
                      height={32}
                    />
                  ),
                },
              ]}
            />
          </>
        }
        onTutorialsChanged={changeTutorialsPreference}
        onAudioEffectsChanged={changeAudioEffectsPreference}
        onDarkModeChanged={changeDarkModePreference}
        onLogout={logout}
        userData={userData}
        onSettingsClick={() => {
          navigate(routes.ACCOUNT)
        }}
      />
    </React.Fragment>
  )
}
